<template>
    <form-wrapper @submit="handleFormSubmission(data)">
        <form-row>
            <form-cell>
                <text-field
                    :label="$t('forms.auth.login.fields.email.label')"
                    :placeholder="$t('forms.auth.login.fields.email.placeholder')"
                    :hasAutofocus="true"
                    :isRequired="true"
                    :formData="data"
                    :error="errorMessage"
                    :isDisabled="isSending || isDisabled"
                    valuePath="email"
                    type="email"
                    @change="changeEmail($event)"
                ></text-field>
            </form-cell>
        </form-row>

        <common-buttons align="center" class="resend-verification">
            <common-button
                v-if="notVerifed"
                :label="$t('forms.auth.login.microcopy.resend')"
                type="primary"
                :isSubmittable="false"
                @click="resendVerification()"
            ></common-button>
        </common-buttons>

        <form-row v-if="!notVerifed">
            <form-cell>
                <text-field
                    :label="$t('forms.auth.login.fields.password.label')"
                    :placeholder="$t('forms.auth.login.fields.password.placeholder')"
                    :isRequired="true"
                    :formData="data"
                    :isDisabled="isSending || isDisabled"
                    valuePath="password"
                    :type="passwordInputFieldType"
                    :maxLength="40"
                    @change="change($event)"
                >
                    <template #right>
                        <router-link :to="{ name: 'auth.forgot-password' }" tabindex="1">
                            {{ $t('forms.auth.login.microcopy.forgot_password') }}
                        </router-link>
                    </template>
                    <template #icon>
                        <common-toggle-visibility-button
                            @visibility-toggle-switch="showPassword"
                        ></common-toggle-visibility-button>
                    </template>
                </text-field>
            </form-cell>
        </form-row>

        <template #footer>
            <common-buttons v-if="!notVerifed">
                <common-button
                    :isProcessing="isSending || isDisabled"
                    :label="$t('forms.auth.login.microcopy.login')"
                    type="primary"
                ></common-button>
            </common-buttons>
        </template>
        <common-notification ref="commonSuccessNotificationChild" :content="notificationMessage"></common-notification>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import GoogleAnalyticsService from 'services/GoogleAnalyticsService'
    import PasswordToggleVisibilityMixin from 'mixins/PasswordToggleVisibilityMixin'
    import AuthAPI from 'api/AuthAPI'
    import LogsTrackerAPI from 'api/LogsTrackerAPI'

    export default {
        mixins: [FormMixin, PasswordToggleVisibilityMixin],

        props: {
            isDisabled: { type: Boolean, default: false },
        },

        data() {
            return {
                errorMessage: null,
                notVerifed: false,
                notificationMessage: '',
            }
        },

        methods: {
            async getClientIpAddress() {
                return fetch('https://api.ipify.org?format=json')
                    .then((res) => res.json())
                    .then(({ ip }) => ip)
                    .catch(() => null)
            },

            logLoginAttempt({ email, status, error }) {
                return this.getClientIpAddress().then((ipAddress) =>
                    LogsTrackerAPI.create({
                        email,
                        eventType: 'LOGIN',
                        status,
                        ipAddress,
                        userAgent: navigator.userAgent,
                        failureReason: error?.message,
                    }),
                )
            },

            handleFormSubmission({ email, password }) {
                this.emitEvent({ name: 'form-submitted', payload: { areFieldsDisabled: true } })
                this.handleRequest(AuthAPI.logIn(email, password))
                    .then((AuthenticationResult) => {
                        const { accessToken, idToken, refreshToken } = AuthenticationResult.signInUserSession
                        this.$auth.logIn(accessToken.jwtToken, idToken.jwtToken, refreshToken.token)

                        GoogleAnalyticsService.emitLogInGoogleAnalytics(this.$ga)
                        return this.logLoginAttempt({ email, status: 'SUCCESS' }).catch(() => {})
                    })
                    .then(() => {
                        this.emitEvent({ name: 'sent' })
                    })
                    .catch((error) => {
                        this.emitEvent({ name: 'form-submitted', payload: { areFieldsDisabled: false } })
                        if (error.code === 'UserNotConfirmedException') {
                            this.errorMessage = this.$t('forms.auth.login.microcopy.not_verified')
                            this.notVerifed = true
                        } else {
                            this.errorMessage = this.$t('forms.auth.login.microcopy.not_found_error')
                        }
                        this.data.password = ''
                        return this.logLoginAttempt({ email, status: 'FAILURE', error })
                    })
                    .finally(() => {
                        this.isSending = false
                    })
            },

            changeEmail(event) {
                this.change(event)
                this.errorMessage = ''
            },

            resendVerification() {
                this.handleRequest(AuthAPI.resendVerificationEmail(this.data.email))
                    .then(() => {
                        this.notVerifed = false
                        this.errorMessage = ''
                        this.notificationMessage = this.$t('forms.auth.login.microcopy.email_sent')
                        this.$refs.commonSuccessNotificationChild.showNotification()
                    })
                    .catch(() => {
                        this.notVerifed = false
                        this.errorMessage = ''
                    })
                    .finally(() => {
                        this.isSending = false
                    })
            },
        },

        mounted() {
            if (this.$route.query.email) {
                this.data.email = this.$route.query.email
            }
        },
    }
</script>

<style scoped lang="scss">
    .resend-verification {
        margin-top: 20px;
    }
</style>
